import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SoloShell from './SoloShell';
import { api } from './api';
import './BuzzerSolo.css';

export default function BuzzerSoloBillingPage() {
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [newQuota, setNewQuota] = useState('');
  const [preview, setPreview] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [activationQuota, setActivationQuota] = useState('20');
  const [activationPeriod, setActivationPeriod] = useState('monthly');
  const [activationPreview, setActivationPreview] = useState(null);

  const onboarding = useMemo(() => new URLSearchParams(location.search).get('onboarding') === '1', [location.search]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/buzzersolo/me');
      setInfo(data);
      setNewQuota(String(data.tableQuota || ''));
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to load billing data');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const previewActivation = async () => {
    try {
      const { data } = await api.post('/buzzersolo/activation/preview', {
        tableQuota: Number(activationQuota),
        billingPeriod: activationPeriod,
      });
      setActivationPreview(data);
      setMessage('Activation preview calculated.');
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to preview activation');
    }
  };

  const startActivationPayment = async () => {
    try {
      setBusy(true);
      let previewData = activationPreview;
      if (!previewData || Number(previewData.tableQuota) !== Number(activationQuota) || previewData.billingPeriod !== activationPeriod) {
        const { data } = await api.post('/buzzersolo/activation/preview', {
          tableQuota: Number(activationQuota),
          billingPeriod: activationPeriod,
        });
        previewData = data;
        setActivationPreview(data);
      }

      const merchantTrns = `SOLO_ACTIVATION|quota=${Number(activationQuota)}|period=${activationPeriod}|ts=${Date.now()}`;
      const customerTrns = `BuzzerFeedback Solo activation (${Number(activationQuota)} tables / ${activationPeriod})`;

      const { data } = await api.post('/_payments/viva/create-order', {
        amountMinor: Number(previewData.amountMinor),
        customerTrns,
        merchantTrns,
        tags: ['buzzerfeedback_solo', 'activation'],
      });

      if (!data?.checkoutUrl) {
        throw new Error('Viva checkout URL was not returned');
      }

      window.location.assign(data.checkoutUrl);
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to create Viva order');
    } finally {
      setBusy(false);
    }
  };

  const previewUpgrade = async () => {
    try {
      const { data } = await api.post('/buzzersolo/table-quota/preview', { newQuota: Number(newQuota) });
      setPreview(data);
      setMessage('Preview calculated.');
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to preview upgrade');
    }
  };

  const applyPaidUpgrade = async () => {
    try {
      await api.post('/buzzersolo/table-quota/apply-paid-upgrade', {
        newQuota: Number(newQuota),
        paymentReference,
      });
      setMessage('Paid upgrade applied.');
      setPreview(null);
      setPaymentReference('');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to apply paid upgrade');
    }
  };

  const isActive = info && (info.status === 'active' || info.status === 'trial');

  return (
    <SoloShell
      title="Solo Billing"
      subtitle={
        isActive
          ? 'Manage your current Solo quota and apply paid table increases after Viva verification.'
          : 'Choose your table count, pay online with Viva and activate BuzzerFeedback Solo.'
      }
    >
      {message ? <div className="bsolo-card" style={{ marginBottom: 16 }}>{message}</div> : null}

      {info && !isActive && (
        <div className="bsolo-grid cols-2" style={{ marginBottom: 18 }}>
          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>{onboarding ? 'Step 2 of 2 • Activate Solo' : 'Activate BuzzerFeedback Solo'}</h3>
            <div className="bsolo-list">
              <div><strong>Status:</strong> {info.status}</div>
              <div><strong>Product:</strong> BuzzerFeedback Solo</div>
              <div><strong>Flow:</strong> Choose tables → Pay with Viva → Account activates automatically</div>
            </div>
          </div>

          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Activation plan</h3>
            <div className="bsolo-field">
              <label>Tables</label>
              <input type="number" min="1" value={activationQuota} onChange={(e) => setActivationQuota(e.target.value)} />
            </div>
            <div className="bsolo-actions" style={{ marginTop: 14 }}>
              <button className={activationPeriod === 'monthly' ? 'bsolo-btn' : 'bsolo-btn-secondary'} onClick={() => setActivationPeriod('monthly')}>Monthly</button>
              <button className={activationPeriod === 'yearly' ? 'bsolo-btn' : 'bsolo-btn-secondary'} onClick={() => setActivationPeriod('yearly')}>Yearly</button>
            </div>
            <div className="bsolo-actions" style={{ marginTop: 16 }}>
              <button className="bsolo-btn-secondary" onClick={previewActivation}>Preview total</button>
              <button className="bsolo-btn" onClick={startActivationPayment} disabled={busy}>{busy ? 'Redirecting to Viva...' : 'Pay & activate'}</button>
            </div>
            {activationPreview && (
              <div className="bsolo-card" style={{ marginTop: 16 }}>
                <div><strong>Total:</strong> €{activationPreview.totalAmount}</div>
                <div><strong>Unit price:</strong> €{activationPreview.unitPrice} / table / {activationPreview.billingPeriod === 'yearly' ? 'year' : 'month'}</div>
                <div className="bsolo-meta" style={{ marginTop: 6 }}>{activationPreview.note}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {info && isActive && (
        <div className="bsolo-grid cols-2">
          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Current subscription</h3>
            <div className="bsolo-list">
              <div><strong>Status:</strong> {info.status}</div>
              <div><strong>Billing:</strong> {info.billingPeriod}</div>
              <div><strong>Quota:</strong> {info.tableQuota} tables</div>
              <div><strong>Used tables:</strong> {info.usedTables}</div>
              <div><strong>Renewal:</strong> {info.renewalDate || '-'}</div>
            </div>
          </div>

          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Add more tables</h3>
            <div className="bsolo-inline-form">
              <div className="bsolo-field">
                <label>New total quota</label>
                <input type="number" value={newQuota} onChange={(e) => setNewQuota(e.target.value)} />
              </div>
              <div style={{ alignSelf: 'end' }}>
                <button className="bsolo-btn" onClick={previewUpgrade}>Preview charge</button>
              </div>
            </div>
            {preview && (
              <div className="bsolo-card" style={{ marginTop: 16 }}>
                <div><strong>Extra amount:</strong> €{preview.proratedAmount}</div>
                <div className="bsolo-meta" style={{ marginTop: 6 }}>{preview.note}</div>
                <div className="bsolo-field" style={{ marginTop: 12 }}>
                  <label>Payment reference from Viva verification</label>
                  <input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder="e.g. viva-order-12345" />
                </div>
                <div className="bsolo-actions"><button className="bsolo-btn" onClick={applyPaidUpgrade}>Apply paid upgrade</button></div>
              </div>
            )}
          </div>
        </div>
      )}
    </SoloShell>
  );
}
